import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "w-full px-2 sm:px-6 py-4" }
const _hoisted_2 = { class: "max-w-3xl mx-auto mb-4" }
const _hoisted_3 = { class: "flex items-center justify-between bg-blue-50 border border-blue-200 text-blue-900 rounded-lg px-4 py-3" }
const _hoisted_4 = { class: "font-semibold" }
const _hoisted_5 = { class: "bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 max-w-3xl mx-auto" }
const _hoisted_6 = { class: "flex gap-6 mb-4" }
const _hoisted_7 = { class: "flex items-center gap-3 cursor-pointer" }
const _hoisted_8 = { class: "flex items-center gap-3 cursor-pointer" }
const _hoisted_9 = { class: "relative mb-4" }
const _hoisted_10 = { class: "flex items-center gap-3" }
const _hoisted_11 = { class: "flex-1 relative" }
const _hoisted_12 = ["onKeydown"]
const _hoisted_13 = {
  key: 0,
  class: "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
}
const _hoisted_14 = {
  key: 0,
  class: "px-3 py-2 text-sm text-gray-500 flex items-center gap-2"
}
const _hoisted_15 = {
  key: 1,
  class: "px-3 py-2 text-sm text-red-600"
}
const _hoisted_16 = ["onClick"]
const _hoisted_17 = {
  key: 0,
  class: "px-3 py-2 text-sm text-gray-500"
}
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "flex justify-center" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { class: "bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden max-w-3xl mx-auto" }
const _hoisted_22 = { class: "overflow-x-auto" }
const _hoisted_23 = { class: "min-w-full divide-y divide-gray-200" }
const _hoisted_24 = { class: "bg-white divide-y divide-gray-200" }
const _hoisted_25 = { class: "px-6 py-4 whitespace-nowrap text-sm text-gray-900" }
const _hoisted_26 = { class: "px-6 py-4 whitespace-nowrap" }
const _hoisted_27 = ["onClick"]
const _hoisted_28 = { key: 0 }
const _hoisted_29 = { class: "bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto" }
const _hoisted_30 = { class: "text-sm font-medium text-gray-700 mb-2" }
const _hoisted_31 = { class: "space-y-1" }

import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { PageHeader, ModalComp } from '@/components'
import { useNotification } from '@/composables/useNotification'
import type { MeaFormData } from '@/types'
import { 
  getMeaAutocomplete, 
  updateMeaProducts, 
  checkMonthlyMeaBeforeAdd,
} from '@/api/mea'


export default /*@__PURE__*/_defineComponent({
  __name: 'MeaProductView',
  setup(__props) {

const { success: showSuccessToast, error: showErrorToast } = useNotification()

// État local
const newReference = ref('')
const references = ref<string[]>([])
const isLoading = ref(false)
const monthlyLimit = ref(100)
const remainingJetons = ref(100)
const autocompleteResults = ref<Array<{ reference_base: string }>>([])
const showAutocomplete = ref(false)
const selectedIndex = ref(-1)
const autocompleteTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const showConfirmModal = ref(false)
const confirmModalTitle = ref('Confirmation des réassorts de produits')
const confirmModalMessage = ref('')
// États pour mieux informer l'utilisateur lors de la saisie
const isSearching = ref(false)
const lastAutocompleteError = ref<string | null>(null)

const formData = reactive<MeaFormData>({
  // Par défaut, sélectionner "Nouveautés" au lieu de "Reassort"
  // Activer également "Reassort" par défaut pour qu'il soit coché automatiquement
  reassort: true,
  nouveaute: true,
  referenceBase: ''
})

const canSubmit = computed(() => {
  return (formData.reassort || formData.nouveaute) && references.value.length > 0
})

// Libellé dynamique pour le bouton selon la sélection
const typeTextLabel = computed(() => {
  if (formData.reassort && formData.nouveaute) return 'réassort et nouveautés'
  if (formData.reassort) return 'réassort'
  return 'nouveautés'
})

// Méthodes d'autocomplétion
const handleInput = async () => {
  if (autocompleteTimeout.value) {
    clearTimeout(autocompleteTimeout.value)
  }

  const term = newReference.value.trim()
  
  if (term.length < 2) {
    autocompleteResults.value = []
    showAutocomplete.value = false
    isSearching.value = false
    lastAutocompleteError.value = null
    return
  }

  autocompleteTimeout.value = setTimeout(async () => {
    try {
      // Démarrer la recherche, afficher le panneau
      isSearching.value = true
      lastAutocompleteError.value = null
      showAutocomplete.value = true
      const referenceString = references.value.join('~')
      const results = await getMeaAutocomplete(term, referenceString)
      autocompleteResults.value = results
      // Toujours afficher le panneau même si 0 résultat pour informer l'utilisateur
      showAutocomplete.value = true
      selectedIndex.value = -1
      isSearching.value = false
    } catch (error) {
      autocompleteResults.value = []
      isSearching.value = false
      // Garder le panneau ouvert avec un message d'erreur
      lastAutocompleteError.value = error instanceof Error ? error.message : 'Erreur lors de la récupération des suggestions'
      showAutocomplete.value = true
    }
  }, 300)
}

const handleFocus = () => {
  const term = newReference.value.trim()
  if (term.length >= 2) {
    // Réouvrir pour afficher résultats/états (chargement/erreur/aucun)
    showAutocomplete.value = true
  }
}

const handleBlur = () => {
  // Délai pour permettre le clic sur les résultats
  setTimeout(() => {
    showAutocomplete.value = false
  }, 200)
}

const closeAutocomplete = () => {
  showAutocomplete.value = false
  selectedIndex.value = -1
}

const selectNext = () => {
  if (selectedIndex.value < autocompleteResults.value.length - 1) {
    selectedIndex.value++
  }
}

const selectPrevious = () => {
  if (selectedIndex.value > 0) {
    selectedIndex.value--
  }
}

const selectReference = (reference: string) => {
  newReference.value = reference
  showAutocomplete.value = false
  selectedIndex.value = -1
}

// Méthodes
const addReference = async () => {
  let ref = newReference.value.trim()

  // Si un élément est sélectionné dans l'autocomplétion, le prendre en priorité
  if (selectedIndex.value >= 0 && autocompleteResults.value[selectedIndex.value]) {
    ref = autocompleteResults.value[selectedIndex.value].reference_base
  }

  if (!ref) return

  // Empêcher les doublons
  if (references.value.includes(ref)) {
    showErrorToast('Cette référence est déjà dans la liste')
    return
  }

  // Valider que la référence existe via l'autocomplétion
  let isValid = false
  if (autocompleteResults.value.some(r => r.reference_base === ref)) {
    isValid = true
  } else {
    try {
      const results = await getMeaAutocomplete(ref, references.value.join('~'))
      if (results.some(r => r.reference_base === ref)) {
        isValid = true
      }
    } catch (e) {
      // En cas d'erreur réseau/auth, laisser le flux d'erreur normal lors du submit
    }
  }

  if (!isValid) {
    showErrorToast("Référence introuvable. Veuillez sélectionner une référence proposée.")
    return
  }

  references.value.push(ref)
  newReference.value = ''
  autocompleteResults.value = []
  showAutocomplete.value = false
  selectedIndex.value = -1
}

const removeReference = (reference: string) => {
  const index = references.value.indexOf(reference)
  if (index > -1) {
    references.value.splice(index, 1)
  }
}

const submitForm = async () => {
  if (!canSubmit.value) return

  isLoading.value = true

  try {
    // Vérifier la limite mensuelle
    const limitCheck = await checkMonthlyMeaBeforeAdd(references.value.length)

    if (!limitCheck.allowed) {
      showErrorToast(`Désolé, il y a une limitation de ${limitCheck.limit} références par mois ! Vous avez déjà ${limitCheck.currentCount} références ce mois-ci. En ajoutant ${references.value.length} nouvelle(s), vous dépasseriez la limite de ${limitCheck.limit}.`)
      isLoading.value = false
      return
    }

    // Mettre à jour l'affichage des jetons restants dans le message
    const willConsume = references.value.length
    const after = Math.max(0, remainingJetons.value - willConsume)

    // Préparer le message de confirmation
    const typeText = formData.reassort && formData.nouveaute 
      ? 'réassort et nouveautés'
      : formData.reassort 
        ? 'réassort'
        : 'nouveautés'
    
    confirmModalMessage.value = `Êtes-vous sûr de vouloir mettre ces ${references.value.length} produit(s) en ${typeText} ?\n\nJetons: ${remainingJetons.value} ➜ ${after}`
    showConfirmModal.value = true
    isLoading.value = false
  } catch (error: any) {
    showErrorToast(error.message || 'Erreur lors de la vérification')
    isLoading.value = false
  }
}

const confirmSubmit = async () => {
  isLoading.value = true

  try {
    // Mettre à jour les produits
    const updated = await updateMeaProducts(references.value, {
      reassort: formData.reassort,
      nouveautes: formData.nouveaute,
    })

    if (updated > 0) {
      showSuccessToast(`${updated} produit(s) mis en avant avec succès !`)
      references.value = []
      newReference.value = ''
      // Rafraîchir les jetons restants
      await refreshRemainingJetons()
    } else {
      showErrorToast('Ces produits ont déjà été mis en avant aujourd\'hui')
    }
  } catch (error: any) {
    showErrorToast(error.message || 'Erreur lors de la mise à jour des produits')
  } finally {
    isLoading.value = false
  }
}

// Nettoyer le timeout au démontage
onUnmounted(() => {
  if (autocompleteTimeout.value) {
    clearTimeout(autocompleteTimeout.value)
  }
})

const refreshRemainingJetons = async () => {
  try {
    const check = await checkMonthlyMeaBeforeAdd(0)
    monthlyLimit.value = check.limit
    remainingJetons.value = Math.max(0, check.limit - check.currentCount)
  } catch (e) {
    remainingJetons.value = monthlyLimit.value
  }
}

onMounted(() => {
  refreshRemainingJetons()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(PageHeader), {
      title: "Mise en avant des produits",
      subtitle: "Ajoutez vos produits en réassort et nouveautés",
      class: "mb-0 pr-10"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex items-center gap-2" }, [
            _createElementVNode("i", { class: "fas fa-coins" }),
            _createElementVNode("span", { class: "text-sm" }, "Jetons restants ce mois-ci")
          ], -1)),
          _createElementVNode("div", _hoisted_4, _toDisplayString(remainingJetons.value) + " / " + _toDisplayString(monthlyLimit.value), 1)
        ])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _cache[11] || (_cache[11] = _createElementVNode("h3", { class: "text-lg font-medium text-gray-900 mb-4" }, "Gestion des MEA de produits", -1)),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("label", _hoisted_7, [
            _withDirectives(_createElementVNode("input", {
              type: "checkbox",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.reassort) = $event)),
              class: "w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            }, null, 512), [
              [_vModelCheckbox, formData.reassort]
            ]),
            _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-sm font-medium text-gray-700" }, "Reassort", -1))
          ]),
          _createElementVNode("label", _hoisted_8, [
            _withDirectives(_createElementVNode("input", {
              type: "checkbox",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.nouveaute) = $event)),
              class: "w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            }, null, 512), [
              [_vModelCheckbox, formData.nouveaute]
            ]),
            _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-sm font-medium text-gray-700" }, "Nouveautés", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-2" }, " Tapez une Référence base : ", -1)),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _withDirectives(_createElementVNode("input", {
                type: "text",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newReference).value = $event)),
                onInput: handleInput,
                onKeydown: [
                  _withKeys(_withModifiers(addReference, ["prevent"]), ["enter"]),
                  _withKeys(_withModifiers(selectNext, ["prevent"]), ["down"]),
                  _withKeys(_withModifiers(selectPrevious, ["prevent"]), ["up"]),
                  _withKeys(closeAutocomplete, ["escape"])
                ],
                onBlur: handleBlur,
                onFocus: handleFocus,
                placeholder: "Référence base",
                class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              }, null, 40, _hoisted_12), [
                [
                  _vModelText,
                  newReference.value,
                  void 0,
                  { trim: true }
                ]
              ]),
              (showAutocomplete.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                    (isSearching.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_14, _cache[7] || (_cache[7] = [
                          _createElementVNode("i", { class: "fas fa-spinner fa-spin" }, null, -1),
                          _createTextVNode(" Recherche en cours... ")
                        ])))
                      : (lastAutocompleteError.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, _toDisplayString(lastAutocompleteError.value), 1))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(autocompleteResults.value, (result, index) => {
                              return (_openBlock(), _createElementBlock("div", {
                                key: index,
                                onClick: ($event: any) => (selectReference(result.reference_base)),
                                class: _normalizeClass([
                    'px-3 py-2 cursor-pointer hover:bg-blue-50',
                    selectedIndex.value === index ? 'bg-blue-100' : ''
                  ])
                              }, _toDisplayString(result.reference_base), 11, _hoisted_16))
                            }), 128)),
                            (autocompleteResults.value.length === 0)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_17, " Aucun résultat "))
                              : _createCommentVNode("", true)
                          ], 64))
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("button", {
              onClick: addReference,
              disabled: !newReference.value.trim() || isLoading.value,
              class: "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            }, _cache[8] || (_cache[8] = [
              _createElementVNode("i", { class: "fas fa-plus mr-2" }, null, -1),
              _createTextVNode(" Ajouter ")
            ]), 8, _hoisted_18)
          ])
        ]),
        _cache[12] || (_cache[12] = _createElementVNode("hr", { class: "my-4 border-gray-200" }, null, -1)),
        _createElementVNode("div", _hoisted_19, [
          _createElementVNode("button", {
            onClick: submitForm,
            disabled: !canSubmit.value || isLoading.value || references.value.length > remainingJetons.value || remainingJetons.value === 0,
            class: "inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          }, [
            _cache[10] || (_cache[10] = _createElementVNode("i", { class: "fas fa-check mr-2" }, null, -1)),
            _createTextVNode(" Ajouter ces produits en " + _toDisplayString(typeTextLabel.value), 1)
          ], 8, _hoisted_20)
        ])
      ]),
      _createElementVNode("div", _hoisted_21, [
        _createElementVNode("div", _hoisted_22, [
          _createElementVNode("table", _hoisted_23, [
            _cache[15] || (_cache[15] = _createElementVNode("thead", { class: "bg-gray-50" }, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Référence "),
                _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, " Actions ")
              ])
            ], -1)),
            _createElementVNode("tbody", _hoisted_24, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(references.value, (reference) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: reference,
                  class: "hover:bg-gray-50"
                }, [
                  _createElementVNode("td", _hoisted_25, _toDisplayString(reference), 1),
                  _createElementVNode("td", _hoisted_26, [
                    _createElementVNode("button", {
                      onClick: ($event: any) => (removeReference(reference)),
                      class: "inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    }, _cache[13] || (_cache[13] = [
                      _createElementVNode("i", { class: "fas fa-trash mr-1" }, null, -1),
                      _createTextVNode(" Supprimer ")
                    ]), 8, _hoisted_27)
                  ])
                ]))
              }), 128)),
              (references.value.length === 0)
                ? (_openBlock(), _createElementBlock("tr", _hoisted_28, _cache[14] || (_cache[14] = [
                    _createElementVNode("td", {
                      colspan: "2",
                      class: "px-6 py-8 text-center text-sm text-gray-500"
                    }, " Aucune référence ajoutée ", -1)
                  ])))
                : _createCommentVNode("", true)
            ])
          ])
        ])
      ])
    ]),
    _createVNode(_unref(ModalComp), {
      isVisible: showConfirmModal.value,
      "onUpdate:isVisible": _cache[3] || (_cache[3] = ($event: any) => ((showConfirmModal).value = $event)),
      title: confirmModalTitle.value,
      alert: confirmModalMessage.value,
      cancelText: "Annuler",
      confirmText: "Confirmer",
      size: "sm",
      onConfirm: confirmSubmit
    }, {
      body: _withCtx(() => [
        _createElementVNode("div", _hoisted_29, [
          _createElementVNode("p", _hoisted_30, " Références à mettre en avant (" + _toDisplayString(references.value.length) + ") : ", 1),
          _createElementVNode("ul", _hoisted_31, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(references.value, (ref, index) => {
              return (_openBlock(), _createElementBlock("li", {
                key: index,
                class: "text-sm text-gray-600 flex items-center"
              }, [
                _cache[16] || (_cache[16] = _createElementVNode("i", { class: "fas fa-check-circle text-green-500 mr-2 text-xs" }, null, -1)),
                _createTextVNode(" " + _toDisplayString(ref), 1)
              ]))
            }), 128))
          ])
        ])
      ]),
      _: 1
    }, 8, ["isVisible", "title", "alert"])
  ], 64))
}
}

})